/**
 * THE FOUR ANSWERS, SIDE BY SIDE — #303.
 *
 * Six stacked panels became four in a grid that fits one screen. Vinnie, 27
 * August 2026: *"I cannot scroll past What's stuck and then go to another bar
 * and then another bar and then the epic."* The maths is in src/insights.ts so
 * it can be checked without a browser; what is here is the drawing.
 *
 * EVERY PANEL IS A WAY IN. A number you cannot open is a number you then have
 * to go and find by hand, so a row opens the card and a bar opens the backlog
 * filtered to it — and both leave a way back, which is what `drillTo` is for.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../store';
import { wayBackLabel } from '../view-titles';
import { DEFAULT_FILTERS, FILTER_NOBODY, type Filters } from '../filters';
import { FILTER_NO_AREA, NO_AREA } from '../areas-report';
import {
  DIMENSION_LABEL,
  cumulativeFlow,
  cycleTime,
  forecast,
  inFlight,
  keepingUp,
  leadTime,
  pushedDates,
  splitBy,
  splitByAreaName,
  throughput,
  FORECAST_MIN_FINISHED,
  FORECAST_MIN_WEEKS,
  type Dimension,
  type Flight,
  type Forecast,
  type FlowStage,
  type FlowWeek,
  type Part,
  type SplitRow,
} from '../insights';
import { slipLabel } from '../card-slip';
import { weeksFor } from '../insights-scope';
import { PACE_WINDOW_WEEKS } from '../pace';
import { goLiveTargetMs, livePhases } from '../phases';
import { MONTHS } from '../utils';
import { unblockActions } from '../unblock';
import type { Build, Issue } from '../types';
import type { Holiday } from '../work-calendar';
import Icon from './Icon';

/**
 * WHAT EVERY PANEL READS — the work in scope, and the window over it.
 *
 * One hook so every read on the screen answers the scope toggle and the window
 * picker the same way. On "this board" it is the board that is open; on "all
 * boards" it is every board's cards flattened together — flow only, from the
 * roll-up the store loads when the scope is chosen. Builds are a per-board
 * release fact, so across boards there is no single cadence to show and the reads
 * that touch them get none.
 */
const NO_BUILDS: Build[] = [];
export function useInsightsData(): { issues: Issue[]; builds: Build[]; windowDays: number; allBoards: boolean } {
  const scope = useApp((s) => s.insightsScope);
  const windowDays = useApp((s) => s.insightsWindow);
  const boardIssues = useApp((s) => s.issues);
  const boardBuilds = useApp((s) => s.builds);
  const portfolioIssues = useApp((s) => s.portfolioIssues);
  const allBoards = scope === 'all';
  const issues = useMemo(
    () => (allBoards ? [...portfolioIssues.values()].flat() : boardIssues),
    [allBoards, boardIssues, portfolioIssues]
  );
  return { issues, builds: allBoards ? NO_BUILDS : boardBuilds, windowDays, allBoards };
}

/* ── The card that opens when you hover a bar ─────────────────────────────── */

/**
 * WHAT A BAR IS MADE OF, on hover and on keyboard focus.
 *
 * Asked for by name — Vinnie, 28 August 2026: *"When you hover over the bar
 * graphs an info card should popup with more insights."* All three charts on
 * this screen have one, and they are all this component, because the same fact
 * written three ways in three places reads as three facts.
 *
 * IT ADDS; IT NEVER REPEATS. The bar already says how many. A card that says
 * how many again is the number a second time, bigger. So each one carries what
 * the chart CANNOT show — what the bar is made of, whose desk it is on, how
 * fast that is.
 *
 * ⚠️ NOTHING LIVES ONLY IN HERE, and that is a hard rule rather than a nicety.
 * A hover does not exist on a phone and does not exist for somebody driving by
 * keyboard, so:
 *
 *   · the trigger is a BUTTON with an `aria-label` carrying the same facts in
 *     words, and this card is `aria-hidden` so a screen reader is not read the
 *     same thing twice;
 *   · the card opens on `:focus-visible` as well as `:hover`, so tabbing to a
 *     bar shows it;
 *   · and clicking the bar still opens exactly those cards in the Backlog,
 *     which is the route that works everywhere.
 */
function Card({ head, sub, warn, children }: {
  head: string;
  sub?: ReactNode;
  warn?: boolean;
  children?: ReactNode;
}) {
  return (
    <span className="hovercard" aria-hidden="true">
      <span className="hc-head">{head}</span>
      {sub && <span className={`hc-sub ${warn ? 'is-warn' : ''}`}>{sub}</span>}
      {children}
    </span>
  );
}

/** A block of named counts inside a card — "In review 8", "Vinnie 5". */
function Parts({ of, cap = 4 }: { of: Part[]; cap?: number }) {
  if (of.length === 0) return null;
  const rest = of.length - cap;
  return (
    <span className="hc-parts">
      {of.slice(0, cap).map((p) => (
        <span className="hc-row" key={p.label}>
          <span className="hc-label">{p.label}</span>
          <span className="hc-n">{p.n}</span>
        </span>
      ))}
      {rest > 0 && <span className="hc-more">and {rest} more</span>}
    </span>
  );
}

/**
 * The same counts as a sentence, for the button's own label.
 *
 * `named` is not a formatting preference. Stages read as "6 in progress"; people
 * do not — lowercasing their label produced "held by 1 claude, 1 dominic reyes",
 * which is a screen reader saying somebody's name wrong.
 */
const say = (of: Part[], named = false) =>
  of.map((p) => (named ? `${p.label} ${p.n}` : `${p.n} ${p.label.toLowerCase()}`)).join(', ');

/** A week's date for a hovercard head — "12 Aug". Uses the app's own month
 *  list (utils.MONTHS) rather than toLocaleDateString, whose en-GB short month
 *  renders September as "Sept" — the house short form is "Sep" everywhere else. */
const WEEK_MS = 7 * 86_400_000;
const weekOf = (t: number) => { const d = new Date(t); return `${d.getDate()} ${MONTHS[d.getMonth()]}`; };

/* Day labels for the CFD's day-level scrub. Short "Thu 4 Sep" for the axis chip,
   long "Thursday, 4 Sep" for the hovercard head — the same date, said at the two
   lengths the two places have room for. */
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayChip = (t: number) => { const d = new Date(t); return `${DOW_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`; };
const dayLong = (t: number) => { const d = new Date(t); return `${DOW_LONG[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`; };

/**
 * SCRUB — WHERE ARE YOU ON A TIME-SERIES CHART. Shared by the cumulative-flow
 * chart and the throughput sparkline so the two read as one instrument (Vinnie,
 * 25 Sep). The pointer (or the arrow keys) picks the column of `count` points;
 * `active` is that index, or null when the cursor has left.
 *
 * ONE READING, NEVER TWO, NEVER DELAYED. There is no dwell timer any more. The
 * old build tried to hold a floating detail card back by 0.8s so it would not
 * open on top of the bar you were dragging — Vinnie, 26 Sep: *"The 0.8 sec delay
 * does not fix the problem, there has to be a better way to do this."* The better
 * way is not to float the detail over the cursor at all: the line, dot and small
 * value chip track the cursor at once (immediate, right at the point), and the
 * FULL breakdown lives in a fixed readout pinned above the plot (see
 * <ScrubReadout>) that updates live but never moves under the cursor. So there is
 * nothing to time, and nothing to collide with.
 */
function useScrub(count: number) {
  /* `active` is the INTEGER column under the cursor (nearest day/week) — it drives
     the readout and chip CONTENT and must stay integer. `posX` is the cursor's
     CONTINUOUS position (0..1 of the plot width) — it drives the line/dot/chip
     GEOMETRY so the bar sits exactly under the pointer instead of hopping column
     to column (Vinnie, 27 Sep). The two are set together and stay in step. */
  const [active, setActive] = useState<number | null>(null);
  const [posX, setPosX] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const activeRef = useRef<number | null>(null);
  activeRef.current = active;
  /* Set the active column AND its position together. Used for keyboard, focus and
     blur — anything that lands on a column rather than tracking the pointer, so the
     bar sits on the column's own fractional position i/(count-1). */
  const setAt = useCallback((i: number | null) => {
    setActive(i);
    setPosX(i == null ? null : (count > 1 ? i / (count - 1) : 0));
  }, [count]);
  const onMove = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || count < 1) return;
    const r = el.getBoundingClientRect();
    const frac = r.width > 0 ? (e.clientX - r.left) / r.width : 0;
    /* The bar sits precisely under the cursor (clamped to the plot). */
    setPosX(Math.max(0, Math.min(1, frac)));
    /* The readout still reads the same column the invisible hit cells occupy — n
       equal cells, so the cell under the cursor is floor(frac·n). */
    setActive(Math.max(0, Math.min(count - 1, Math.floor(frac * count))));
  }, [count]);
  const onLeave = useCallback(() => { setActive(null); setPosX(null); }, []);
  const onKey = useCallback((e: React.KeyboardEvent) => {
    if (count < 1) return;
    const a = activeRef.current;
    if (e.key === 'ArrowLeft') { setAt(Math.max(0, (a ?? count) - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { setAt(Math.min(count - 1, (a ?? -1) + 1)); e.preventDefault(); }
    else if (e.key === 'Home') { setAt(0); e.preventDefault(); }
    else if (e.key === 'End') { setAt(count - 1); e.preventDefault(); }
    else if (e.key === 'Escape') setAt(null);
  }, [count, setAt]);
  return { active, posX, setActive: setAt, ref, onMove, onLeave, onKey };
}

/**
 * THE ONE DETAIL READOUT for a time-series chart — pinned above the plot, out of
 * the scrub's way, updating live as the cursor (or the arrow keys) move.
 *
 * This is the fix for both the cumulative-flow and throughput bugs (Vinnie, 26
 * Sep): the detail no longer floats over the point you are pointing at, and there
 * is exactly ONE of it — never a chip that pops then a card that replaces it. It
 * sits in a fixed strip so scrubbing changes its numbers in place rather than
 * opening and closing a box. `aria-hidden` because each hit cell already carries
 * the same facts in words for a screen reader.
 */
function ScrubReadout({ head, parts, note }: {
  head: string;
  parts?: Part[];
  note?: ReactNode;
}) {
  return (
    <div className="scrub-readout" aria-hidden="true">
      <span className="sr-head">{head}</span>
      {parts && parts.length > 0 && (
        <span className="sr-parts">
          {parts.map((p) => (
            <span className="sr-part" key={p.label}>
              {p.label} <b className="sr-n">{p.n}</b>
            </span>
          ))}
        </span>
      )}
      {note && <span className="sr-note">{note}</span>}
    </div>
  );
}

/** The axis is short because it is an axis; a sentence needs the words. The
 *  head reads as a whole phrase, so "today" gets "Here" (nothing has been
 *  sitting "for" no time) while the rest take "Sitting for …". */
const BUCKET_SAYS: Record<string, string> = {
  today: 'Here since today',
  '1–5d': 'Sitting for 1–5 days',
  '6–13d': 'Sitting for 6–13 days',
  '2w+': 'Sitting for 2 weeks or more',
};
/* The same buckets as a phrase that follows the word "sitting" — for the backlog
   focus reason ("sitting since today", "sitting for 1–5 days"). Kept apart from
   the head above, which reads as a whole title ("Here since today"). */
const BUCKET_SINCE: Record<string, string> = {
  today: 'since today',
  '1–5d': 'for 1–5 days',
  '6–13d': 'for 6–13 days',
  '2w+': 'for 2 weeks or more',
};

/* ── What is moving, and what has stopped ─────────────────────────────────── */

/**
 * NOT THE LIST IT WAS. Vinnie, 28 August 2026: *"Should the inflight card have
 * a list only? Or can it be expressed some other way?"* It could, and it should
 * — thirty-two rows of everything in progress is the Backlog with two filters
 * on it, and reading all of them was the only way to find the one that had
 * stopped. Now the shape answers "is anything wrong" before you read a word,
 * and the only rows printed are the ones you would actually go and chase.
 */
/**
 * THE AGING-WIP HERO, drawn once and used twice — the Insights lead panel and
 * the board-metrics strip (mock B). Extracting it is what makes "stuck" mean the
 * same thing in both places: both feed it an `inFlight()` result, which counts a
 * card stuck by the same card-age threshold the board's amber marker uses.
 *
 * `canDrill` is the all-boards guard AND the strip's this-board permission in one
 * flag. The backlog is one board, so across boards a bucket or a "sitting
 * longest" row cannot open the cards it counts — the drill would land short (the
 * interaction-witness caught the aging hero doing exactly that in all-boards
 * mode, 24 Sep). When it is false the shape and the hovercards stay — they are a
 * reading, not a control — but the rows stop being buttons and drop the
 * "click to open" line.
 */
export function AgingHeroBody({ flight: f, canDrill, crossBoard = false, onOpen, onShow }: {
  flight: Flight;
  canDrill: boolean;
  /* Across boards there is no single "this board" — the norm is each board's own,
     so the wording must not claim one board (ux witness, 24 Sep). */
  crossBoard?: boolean;
  onOpen: (id: string) => void;
  onShow: (ids: string[], why: string) => void;
}) {
  const norm = crossBoard ? 'usual for their board' : 'usual for this board';
  const stages = [
    f.inprogress.length && `${f.inprogress.length} In Progress`,
    f.staging.length && `${f.staging.length} In Staging`,
    f.inreview.length && `${f.inreview.length} In Review`,
  ].filter(Boolean);
  const tallest = Math.max(...f.buckets.map((x) => x.count));

  return (
    <div className="ah-body">
      <div className="ah-summary">
        <p className="ah-big"><span className="ah-num">{f.stalled}</span> stuck</p>
        <p className="ah-note">
          of {f.total} in flight{stages.length ? ` (${stages.join(' · ')})` : ''}.{' '}
          {f.stalled > 0
            ? `${f.stalled} ${f.stalled === 1 ? 'has' : 'have'} sat longer than ${norm}.`
            : `None have sat longer than ${norm}.`}
        </p>
      </div>
      <div className="ah-mid">
        {/* HOW LONG THINGS HAVE BEEN SITTING, as a shape. Four bars are read in
            one glance; thirty-two rows are read one at a time. */}
        <div className="spread">
          {f.buckets.map((x) => {
            /* An empty bucket is a fact, not a control: there is nothing to open
               and nothing to say about it, so it draws its axis label and stops. */
            if (x.count === 0) {
              return (
                <div key={x.label} className="spread-col hc-anchor">
                  <span className="spread-n" />
                  <span className="spread-bar" style={{ height: 0 }} />
                  <span className="spread-label">{x.label}</span>
                </div>
              );
            }
            const stageStr = [
              x.inprogress && `${x.inprogress} In Progress`,
              x.staging && `${x.staging} In Staging`,
              x.inreview && `${x.inreview} In Review`,
            ].filter(Boolean).join(', ');
            const inner = (
              <>
                <span className="spread-n">{x.count}</span>
                <span
                  className="spread-bar"
                  style={{ height: `${Math.max(3, (x.count / tallest) * 54)}px` }}
                >
                  {/* The amber is the part of the bucket sitting too long, in the
                      same language as the bug head on "Where the work is". */}
                  {x.stalled > 0 && (
                    <span className="spread-old" style={{ height: `${(x.stalled / x.count) * 100}%` }} />
                  )}
                </span>
                <span className="spread-label">{x.label}</span>
                <Card
                  head={BUCKET_SAYS[x.label] ?? x.label}
                  warn={x.stalled > 0}
                  sub={
                    <>
                      {x.count} {x.count === 1 ? 'item' : 'items'}
                      {x.stalled > 0 && <> · <b>{x.stalled} longer than usual</b></>}
                    </>
                  }
                >
                  <Parts of={[
                    { label: 'In Review', n: x.inreview },
                    { label: 'In Staging', n: x.staging },
                    { label: 'In Progress', n: x.inprogress },
                  ].filter((pt) => pt.n > 0)} />
                  <Parts of={x.who} />
                  {canDrill && <span className="hc-cta">Click to open all {x.count} →</span>}
                </Card>
              </>
            );
            /* THE CARD'S FACTS, IN WORDS. A hover is not available to everybody;
               this is, and it is why the card may be hidden. */
            const said =
              `${BUCKET_SAYS[x.label] ?? x.label}: ${x.count} items` +
              (x.stalled ? `, ${x.stalled} longer than usual` : '') +
              `. ${stageStr}. Held by ${say(x.who, true)}.`;
            return canDrill ? (
              <button
                key={x.label}
                type="button"
                className="spread-col hc-anchor"
                aria-label={`${said} Open them.`}
                onClick={() => onShow(x.ids, `sitting ${BUCKET_SINCE[x.label] ?? x.label}`)}
              >
                {inner}
              </button>
            ) : (
              <div key={x.label} className="spread-col hc-anchor" aria-label={said}>
                {inner}
              </div>
            );
          })}
        </div>

        {/* A KEY, because two colours with no names is a puzzle. Asked for by
            name — Vinnie, 28 August 2026: *"there needs to be a key that shows
            what the different colors mean."* It says the same words as the line
            above the chart on purpose: "sitting longer than usual" is the phrase
            this screen uses for it, and a second phrasing for one idea is how a
            reader ends up believing there are two. */}
        <div className="spread-key">
          <span><i className="key-dot is-moving" aria-hidden="true" />Moving at the usual pace</span>
          <span><i className="key-dot is-old" aria-hidden="true" />Sitting longer than usual</span>
        </div>
      </div>{/* .ah-mid */}
      <div className="ah-right">
        {f.longest.length > 0 ? (
          <div className="longest">
            <p className="longest-head">Sitting longest</p>
            {f.longest.slice(0, 4).map((r) => {
              const rowInner = (
                <>
                  <span className="ref">{r.issue.ref}</span>
                  <span className="flight-title">{r.issue.title}</span>
                  {/* The age is the point of this panel, so it is never the thing
                      that gets truncated when the title is long. */}
                  <span className="flight-age is-stalled">{r.days}d</span>
                </>
              );
              return canDrill ? (
                <button key={r.issue.id} className="flight-row" onClick={() => onOpen(r.issue.id)}>
                  {rowInner}
                </button>
              ) : (
                <div key={r.issue.id} className="flight-row" aria-label={`${r.issue.ref} ${r.issue.title}, sitting ${r.days} days`}>
                  {rowInner}
                </div>
              );
            })}
            {f.longest.length > 4 && (
              <p className="longest-more">and {f.longest.length - 4} more sitting longer than usual.</p>
            )}
          </div>
        ) : (
          <p className="hint">Nothing has sat longer than the average for its own stage.</p>
        )}
      </div>{/* .ah-right */}
    </div>
  );
}

export function InFlight() {
  const { issues, allBoards } = useInsightsData();
  const openIssue = useApp((s) => s.openIssue);
  const showInBacklog = useApp((s) => s.showInBacklog);
  /*
   * `Date.now()` once per render rather than once per row: two rows a
   * millisecond apart either side of midnight would otherwise be a day apart.
   */
  const profiles = useApp((s) => s.profiles);
  const f = useMemo(() => inFlight(issues, Date.now(), profiles), [issues, profiles]);

  if (f.total === 0) {
    return (
      <section className="panel aging-hero">
        <h2>Aging Work in Progress</h2>
        <p className="panel-sub">Nothing is being worked on right now.</p>
      </section>
    );
  }

  const settled = f.total - f.stalled;

  return (
    <section className="panel aging-hero">
      <h2>Aging Work in Progress</h2>
      <p className="panel-sub">Time in the working columns, measured from when each card started</p>
      {/* Across boards a drill lands short (the backlog is one board), so the
          hero becomes a reading, not a way in — the same guard "Where the work
          is" uses. */}
      <AgingHeroBody flight={f} canDrill={!allBoards} crossBoard={allBoards} onOpen={openIssue} onShow={showInBacklog} />

      {!allBoards ? (
        <p className="hint">
          {settled > 0 &&
            `The other ${settled} ${settled === 1 ? 'is' : 'are'} moving at this board's usual pace. `}
          <button
            className="link"
            onClick={() =>
              showInBacklog(
                [...f.inprogress, ...f.staging, ...f.inreview].map((r) => r.issue.id),
                'being worked on right now'
              )
            }
          >
            See all {f.total}
          </button>
        </p>
      ) : (
        <p className="hint">
          {settled > 0 &&
            `The other ${settled} ${settled === 1 ? 'is' : 'are'} moving at the usual pace. `}
          Across boards, open a single board to click into its cards.
        </p>
      )}
      {/* The KEY names the colours; this names the rule behind one of them, and
          says it as a definition rather than repeating the key in other words. */}
      {f.stalled > 0 && (
        <p className="hint">
          "Longer than usual" means longer than the average for its own stage {allBoards ? 'on its board' : 'on this board'}.
        </p>
      )}
    </section>
  );
}

/* ── What is stuck, and what would free it ────────────────────────────────── */

export function InYourWay() {
  const { issues, allBoards } = useInsightsData();
  const showInBacklog = useApp((s) => s.showInBacklog);
  const actions = useMemo(() => unblockActions(issues), [issues]);
  const held = actions.reduce((a, x) => a + x.issues.length, 0);

  return (
    <section className="panel">
      <h2>Blockers</h2>
      <p className="panel-sub">
        {actions.length === 0
          ? 'Nothing on this board is waiting on anything.'
          : `${held} ${held === 1 ? 'item' : 'items'}, held up by ${actions.length} ${
              actions.length === 1 ? 'blocker' : 'blockers'
            }`}
      </p>
      {/*
        * GROUPED BY THE THING BEING WAITED ON, not card by card. "Six items are
        * blocked" is not something anybody can act on; "run the load test, it
        * frees six" is. `exact` is the difference between the two — see
        * unblockActions, which refuses to promise a category releases anything.
        */}
      {actions.slice(0, 4).map((a) => {
        const inner = (
          <>
            <span className="stuck-what">
              <span className="stuck-label">{a.label}</span>
              <span className="stuck-detail">
                {a.issues.map((i) => i.ref).join(', ')}
                {a.exact ? ' — clearing this releases all of them' : ' — each may be waiting on a different one'}
              </span>
            </span>
            <span className="stuck-n">{a.issues.length}</span>
          </>
        );
        /* Across boards a drill lands short (the backlog is one board), so the
           row is a reading rather than a way in — the same guard the aging hero,
           "Keeping up?" and "Where the work is" use. */
        return allBoards ? (
          <div key={a.key} className="stuck-row" aria-label={`${a.label}: ${a.issues.length}`}>{inner}</div>
        ) : (
          <button key={a.key} className="stuck-row" onClick={() => showInBacklog(a.issues.map((i) => i.id), 'waiting on that')}>
            {inner}
          </button>
        );
      })}
      {actions.length > 4 && <p className="hint">and {actions.length - 4} more.</p>}
      {allBoards && actions.length > 0 && (
        <p className="hint">Across boards, open a single board to click into what is waiting.</p>
      )}
    </section>
  );
}

/* ── The cards whose target date keeps being pushed later ──────────────────── */

/** How many rows the slippers table shows before it stops and counts the rest. */
const SLIPPERS_SHOWN = 6;

export function PushedDates() {
  const { issues, allBoards } = useInsightsData();
  const openIssue = useApp((s) => s.openIssue);
  const p = useMemo(() => pushedDates(issues), [issues]);

  if (p.count === 0) {
    return (
      <section className="panel">
        <h2>Repeatedly Pushed Dates</h2>
        <p className="panel-sub">
          No card&rsquo;s due date has been pushed later twice while the work was underway.
        </p>
      </section>
    );
  }

  const shown = p.rows.slice(0, SLIPPERS_SHOWN);

  return (
    <section className="panel">
      <h2>Repeatedly Pushed Dates</h2>
      <p className="panel-sub">
        Cards whose due date has moved later two or more times — where the plan keeps slipping under the work.
      </p>

      {/* The three numbers that frame the list: how many cards, how far the
          typical one has drifted, and whether it is concentrated on a few people. */}
      <div className="pushed-stats">
        <div className="pushed-stat">
          <span className="pushed-big is-slip">{p.count}</span>
          <span className="pushed-cap">
            {p.count === 1 ? 'card with a repeatedly-pushed target' : 'cards with repeatedly-pushed targets'}
          </span>
        </div>
        <div className="pushed-stat">
          <span className="pushed-big">{slipLabel(p.medianSlip)}</span>
          <span className="pushed-cap">median slip on those cards</span>
        </div>
        <div className="pushed-stat">
          <span className="pushed-big">{p.ownerCount}</span>
          <span className="pushed-cap">
            {p.ownerCount === 1 ? 'owner holds two or more' : 'owners hold two or more'}
          </span>
        </div>
      </div>

      <div className="pushed-table">
        <div className="pushed-head" aria-hidden="true">
          <span className="pushed-col-name">Biggest slippers</span>
          <span className="pushed-col-bar">Slip</span>
          <span className="pushed-col-later">Later</span>
          <span className="pushed-col-push">Pushes</span>
        </div>
        {shown.map((r) => {
          const inner = (
            <>
              <span className="pushed-name">
                <span className="pushed-ref">{r.ref}</span>
                <span className="pushed-title">{r.title}</span>
              </span>
              {/* The bar carries the slip as length as well as colour, so the
                  ranking reads without reaching the number on the right. */}
              <span className="pushed-bar" aria-hidden="true">
                <i style={{ width: `${p.maxSlip ? Math.max(6, (r.slipDays / p.maxSlip) * 100) : 0}%` }} />
              </span>
              <span className="pushed-later">{slipLabel(r.slipDays)}</span>
              <span className="pushed-push">{r.pushes}× moved</span>
            </>
          );
          const label = `${r.ref} ${r.title}: target ${slipLabel(r.slipDays)}, moved ${r.pushes} times`;
          /* Across boards the card is not in the open board's store, so the row is
             a reading rather than a way in — the same guard the other panels use. */
          return allBoards ? (
            <div key={r.id} className="pushed-row" aria-label={label}>{inner}</div>
          ) : (
            <button key={r.id} className="pushed-row" onClick={() => openIssue(r.id)} aria-label={label}>
              {inner}
            </button>
          );
        })}
        {p.rows.length > SLIPPERS_SHOWN && (
          <p className="hint">and {p.rows.length - SLIPPERS_SHOWN} more.</p>
        )}
      </div>
    </section>
  );
}

/* ── Is the backlog growing or shrinking ──────────────────────────────────── */

export function KeepingUp() {
  const { issues, builds, windowDays, allBoards } = useInsightsData();
  const showInBacklog = useApp((s) => s.showInBacklog);
  const k = useMemo(
    // The counts read the chosen display window; the weeks-to-clear projection reads the canonical
    // 12-week pace window (FF-1), so it agrees with the finish date and the go-live odds.
    () => keepingUp(issues, builds.map((b) => b.date ?? ''), Date.now(), windowDays, PACE_WINDOW_WEEKS * 7),
    [issues, builds, windowDays]
  );
  const most = Math.max(1, k.finished, k.raised, k.shipped);

  /*
   * EACH BAR IS A BUTTON, because each one is a set of cards you can go and
   * look at — except Shipped, which is builds rather than issues and belongs on
   * Releases. A bar with nowhere to go stays a plain row rather than offering a
   * control that does nothing.
   */
  const bar = (
    n: number, cls: string, label: string,
    rate: number, card: ReactNode, sentence: string,
    go?: () => void
  ) => {
    /* NOTHING TO OPEN AND NO RATE TO QUOTE when the bar is empty. Both were
       drawn anyway at first, and read "About 0 a week" above "Click to open
       all 0" — an offer the screen could not keep, on a fact nobody needs. */
    const opens = Boolean(go) && n > 0;
    const inside = (
      <>
        <span className="kbar-label">{label}</span>
        <span className="kbar-track">
          <span className={`kbar-fill ${cls}`} style={{ width: `${(n / most) * 100}%` }} />
        </span>
        <span className="kbar-n">{n}</span>
        <Card head={label} sub={`${n} in the last ${k.days} days`}>
          {n > 0 && (
            <span className="hc-line">
              {/* THE RATE IS THE POINT. A bar of 52 does not say how fast that
                  is, and "about 13 a week" is the sentence somebody repeats. */}
              About <b>{rate}</b> a week.
            </span>
          )}
          {card}
          {opens && <span className="hc-cta">Click to open all {n} →</span>}
        </Card>
      </>
    );
    const said =
      `${label}: ${n} in the last ${k.days} days` +
      (n > 0 ? `, about ${rate} a week` : '') + `. ${sentence}`;
    return opens ? (
      <button type="button" className="kbar is-open hc-anchor" aria-label={`${said} Open them.`} onClick={go}>
        {inside}
      </button>
    ) : (
      <div className="kbar hc-anchor" aria-label={said}>{inside}</div>
    );
  };

  return (
    <section className="panel">
      <h2>Net Flow</h2>
      <p className="panel-sub">Last {k.days} days</p>
      {bar(
        k.finished, 'is-finished', 'Finished', k.perWeek.finished,
        <span className="hc-line">
          {k.raised > k.finished
            ? <>Fewer than the <b>{k.raised}</b> raised, so the backlog grew.</>
            : k.raised < k.finished
              ? <>More than the <b>{k.raised}</b> raised, so the backlog shrank.</>
              : <>The same as the <b>{k.raised}</b> raised.</>}
        </span>,
        k.raised > k.finished ? 'Fewer than were raised.' : 'More than were raised.',
        /* Across boards a drill lands short (the backlog is one board), so the
           bar becomes a plain reading rather than a way in — the same guard the
           aging hero and "Where the work is" use. */
        allBoards ? undefined : () => showInBacklog(k.finishedIds, `finished in the last ${k.days} days`)
      )}
      {bar(
        k.raised, 'is-raised', 'Raised', k.perWeek.raised,
        <span className="hc-line">
          {k.weeksToClear !== null
            ? <>At the last {PACE_WINDOW_WEEKS} weeks’ finishing pace, the <b>{k.open}</b> open take about <b>{k.weeksToClear}</b>{' '}
              {k.weeksToClear === 1 ? 'week' : 'weeks'}.</>
            : <>Nothing finished in the last {PACE_WINDOW_WEEKS} weeks, so there is no pace to judge the <b>{k.open}</b> open by.</>}
        </span>,
        `${k.open} open in total.`,
        allBoards ? undefined : () => showInBacklog(k.raisedIds, `raised in the last ${k.days} days`)
      )}
      {bar(
        k.shipped, 'is-shipped', 'Shipped', k.perWeek.shipped,
        <span className="hc-line">Builds that went out, not cards. They live on Releases.</span>,
        'Builds that went out.'
      )}
      <p className="keeping-verdict">
        {/*
          * SAID IN WORDS, not left as three bars to compare. The comparison IS
          * the answer, and making somebody do it themselves is the difference
          * between a chart and an insight.
          */}
        {k.growth > 0
          ? `The backlog grew by ${k.growth} over this period.`
          : k.growth < 0
            ? `The backlog shrank by ${-k.growth} over this period.`
            : 'The backlog is the same size as it was.'}
        {k.weeksToClear !== null
          ? ` At the last ${PACE_WINDOW_WEEKS} weeks’ finishing pace the ${k.open} open take about ${k.weeksToClear} ${
              k.weeksToClear === 1 ? 'week' : 'weeks'
            }.`
          : ` Nothing finished in the last ${PACE_WINDOW_WEEKS} weeks, so there is no pace to judge the ${k.open} open by.`}
      </p>
    </section>
  );
}

/* ── Flow health: how much is finishing, and how fast ─────────────────────── */

/**
 * CARDS FINISHED PER WEEK, AS A SHAPE — not just a rate.
 *
 * The big number is the average; the sparkline is the ten weeks it averages, so
 * a run of empty weeks or a burst after one shows rather than being flattened
 * into "about six a week". The maths — including which way the pace is drifting
 * — is in src/insights.ts so it can be checked without a browser.
 */
export function Throughput() {
  const { issues, windowDays } = useInsightsData();
  const t = useMemo(() => throughput(issues, Date.now(), weeksFor(windowDays)), [issues, windowDays]);
  // The TREND CONCLUSION (speeding / slowing / steady) reads the canonical pace window, not the
  // chosen display window (FF-1): "speeding up" must never point opposite the finish date and the
  // go-live odds, which read the same 12 weeks. The sparkline + the per-week average above still
  // follow the window the explorer picked; only the direction word binds to the pace window.
  const tTrend = useMemo(() => throughput(issues, Date.now(), PACE_WINDOW_WEEKS), [issues]);

  const W = 220, H = 40, pad = 3;
  const max = Math.max(1, ...t.series.map((s) => s.n));
  const span = t.series.length > 1 ? (W - pad * 2) / (t.series.length - 1) : 0;
  const xy = (n: number, i: number) => ({
    x: pad + i * span,
    y: pad + (1 - n / max) * (H - pad * 2),
  });
  const pts = t.series.map((s, i) => { const p = xy(s.n, i); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; });
  const avg = Math.round(t.perWeek);

  /* The scrub line replaces the fixed endpoint dot: the reference point is
     wherever you are, not always the last week. Throughput is weekly, so it
     snaps week to week (day-level belongs to the flow chart). */
  const scrub = useScrub(t.series.length);
  const a = scrub.active;
  const as = a != null ? t.series[a] : null;
  const leftPct = (i: number) => (xy(0, i).x / W) * 100;
  /* The readout reads wherever the scrub is, and rests on the latest week when
     the cursor has left — so there is always one reading, in one fixed place. */
  const ri = a ?? t.series.length - 1;
  const rs = t.series[ri];

  return (
    <section className="panel flow-card">
      <h2>Throughput</h2>
      <p className="panel-sub">Cards finished per week (by completion date)</p>
      <p className="flow-big"><span className="flow-num">{Math.round(t.perWeek)}</span> / week</p>
      {/* THE FIXED READOUT — the week's number and how it sits against the
          average, pinned here above the line so it never rides over the point you
          are scrubbing. It follows the cursor's column and rests on the latest
          week when the cursor leaves. */}
      <ScrubReadout
        head={`Week of ${weekOf(rs.from)}`}
        note={
          rs.n === 0
            ? <>Nothing finished this week.</>
            : rs.n >= avg
              ? <><b>{rs.n}</b> {rs.n === 1 ? 'card' : 'cards'} finished &mdash; at or above the <b>{avg}</b>/week average.</>
              : <><b>{rs.n}</b> {rs.n === 1 ? 'card' : 'cards'} finished &mdash; below the <b>{avg}</b>/week average.</>
        }
      />
      {/* THE LINE IS THE SHAPE; the marker follows the cursor. Each week is an
          invisible hit column reachable by keyboard (role="img" carries the facts
          in words); pointing or focusing one moves the marker and the readout. */}
      <div className="spark-wrap" ref={scrub.ref} onMouseMove={scrub.onMove} onMouseLeave={scrub.onLeave}>
        <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          <polyline points={pts.join(' ')} fill="none" />
        </svg>
        {a != null && as && (() => {
          /* CONTINUOUS geometry: the line/dot/chip sit at the cursor's own x
             (posX), not the active column's x, so they glide instead of hopping.
             The dot's height rides the line by interpolating the count between the
             two flanking weeks, so it follows the shape between points. The chip
             CONTENT still reads the nearest week (`as`). */
          const px = scrub.posX ?? leftPct(a) / 100;
          const idx = px * (t.series.length - 1);
          const lo = Math.floor(idx), hi = Math.ceil(idx), f = idx - lo;
          const nAt = t.series[lo].n + (t.series[hi].n - t.series[lo].n) * f;
          const dotY = pad + (1 - nAt / max) * (H - pad * 2);
          return (
            <>
              <div className="scrub-line" style={{ left: `${px * 100}%` }} />
              <div className="scrub-dot" style={{ left: `${px * 100}%`, top: `${(dotY / H) * 100}%` }} />
              {/* The lightweight marker chip, right at the cursor — date and count,
                  the immediate reading. The full breakdown is the fixed readout above. */}
              <div className="scrub-chip" style={{ left: `${px * 100}%` }}>Wk of {weekOf(as.from)} · {as.n}</div>
            </>
          );
        })()}
        <div className="spark-hits">
          {t.series.map((s, i) => (
            <span
              key={s.from}
              className="spark-hit"
              tabIndex={0}
              role="img"
              aria-label={`Week of ${weekOf(s.from)}: ${s.n} finished${s.n === 0 ? '' : s.n >= avg ? `, at or above the ${avg} a week average` : `, below the ${avg} a week average`}`}
              onFocus={() => scrub.setActive(i)}
              onBlur={() => scrub.setActive(null)}
            />
          ))}
        </div>
      </div>
      <p className="flow-note">
        {tTrend.trend === 'steady'
          ? <>Steady over {tTrend.weeks} weeks &mdash; <b>no slowdown, no spike</b>.</>
          : tTrend.trend === 'speeding'
            ? <>Speeding up over {tTrend.weeks} weeks &mdash; <b>more finishing lately</b>.</>
            : <>Slowing over {tTrend.weeks} weeks &mdash; <b>fewer finishing lately</b>.</>}
      </p>
    </section>
  );
}

/**
 * HOW LONG A CARD TAKES, START TO DONE — the median, its drift, and the tail.
 *
 * Measured from startedAt to completedAt, so it is time spent being worked, not
 * time since it was raised. A card missing either stamp is left out rather than
 * counted as instant; when nothing in the window can be timed the card says so
 * instead of drawing a confident "0 days".
 */
export function CycleTime() {
  const { issues, windowDays } = useInsightsData();
  const c = useMemo(() => cycleTime(issues, Date.now(), windowDays), [issues, windowDays]);
  const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

  if (c.sample === 0) {
    return (
      <section className="panel flow-card">
        <h2>Cycle Time</h2>
        <p className="panel-sub">Start &rarr; done, this board</p>
        <p className="flow-note">
          Nothing has finished with both a start and a finish recorded in the last {c.days} days, so there is no time to measure yet.
        </p>
      </section>
    );
  }

  return (
    <section className="panel flow-card">
      <h2>Cycle Time</h2>
      <p className="panel-sub">Start &rarr; done, this board</p>
      {/* A card that starts and finishes the same day rounds to zero whole days;
          "under a day" is the truth, "0 days" reads as broken. */}
      <p className="flow-big">
        {c.median >= 1
          ? <><span className="flow-num">{c.median}</span> {c.median === 1 ? 'day' : 'days'} median</>
          : <><span className="flow-num">&lt;1</span> day median</>}
      </p>
      {c.delta !== null && c.delta !== 0 && (
        <p className={`flow-trend ${c.delta < 0 ? 'is-good' : 'is-warn'}`}>
          {c.delta < 0
            ? <>&#9660; {days(-c.delta)} faster than the previous month.</>
            : <>&#9650; {days(c.delta)} slower than the previous month.</>}
        </p>
      )}
      {c.delta === 0 && <p className="flow-trend">About the same as the previous month.</p>}
      <p className="flow-note">
        Half finish inside {c.median >= 1 ? days(c.median) : 'a day'};{' '}
        the slowest 1 in 10 take {c.p90 >= 1 ? `${days(c.p90)} or more` : 'under a day'}.
      </p>
    </section>
  );
}

/**
 * HOW LONG A CARD TAKES RAISED-TO-DONE, AND HOW MUCH IS THE WAIT — the lead-time
 * panel (#439, split from cycle time #234).
 *
 * Cycle time says "once we start, four days"; this says "from raised to done,
 * fifteen" — and the split bar draws the gap so nobody subtracts: a grey Waiting
 * segment (the backlog wait) then a green Working segment, the exact span Cycle
 * time shows. All three medians are derived from timestamps already captured; a
 * card missing a start is left out, the same honesty as cycle time, so the two
 * panels time one set of cards. The month-trend line underneath is Insights-only
 * (the board strip carries the split bar alone) — a rising line is a growing wait.
 */
export function LeadTime() {
  const { issues, windowDays } = useInsightsData();
  const l = useMemo(() => leadTime(issues, Date.now(), windowDays), [issues, windowDays]);
  const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
  const monthName = (t: number) => { const d = new Date(t); return `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`; };

  if (l.sample === 0) {
    return (
      <section className="panel flow-card">
        <h2>Lead Time</h2>
        <p className="panel-sub">Raised &rarr; done, this board</p>
        <p className="flow-note">
          Nothing has finished with both a start and a finish recorded in the last {l.days} days, so there is no lead time to measure yet.
        </p>
      </section>
    );
  }

  /* The wait segment is the remainder of the lead median (lead − work), so
     Waiting + Working add up to the hero — the bar decomposes the headline and
     nobody has to subtract. The wait leads the bar because it is usually the
     bigger, less-seen number. */
  const total = Math.max(1, l.waitMedian + l.workMedian);
  const waitPct = (l.waitMedian / total) * 100;
  const waitLeads = l.waitMedian >= l.workMedian;

  /* The month-trend sparkline — lead median across the calendar months that had
     something to time. A month with nothing finished is left OUT, not drawn as a
     zero (which would read as "lead time was nil, then grew"). A plain shape, not
     the scrubbed instrument, so the split bar stays the subject; a marker dots the
     latest month. Direction is read from the first and last months with data. */
  const seen = l.months.filter((m) => m.sample > 0);
  const W = 220, H = 40, pad = 3;
  const max = Math.max(1, ...seen.map((m) => m.median));
  const span = seen.length > 1 ? (W - pad * 2) / (seen.length - 1) : 0;
  const xy = (v: number, i: number) => ({ x: pad + i * span, y: pad + (1 - v / max) * (H - pad * 2) });
  const pts = seen.map((m, i) => { const p = xy(m.median, i); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; });
  const dir = seen.length >= 2
    ? (seen[seen.length - 1].median - seen[0].median >= 1 ? 'rising'
      : seen[0].median - seen[seen.length - 1].median >= 1 ? 'shortening' : 'steady')
    : 'steady';

  return (
    <section className="panel flow-card">
      <h2>Lead Time</h2>
      <p className="panel-sub">Raised &rarr; done, this board</p>
      <p className="flow-big">
        {l.median >= 1
          ? <><span className="flow-num">{l.median}</span> {l.median === 1 ? 'day' : 'days'} median</>
          : <><span className="flow-num">&lt;1</span> day median</>}
      </p>
      {/* THE SPLIT BAR IS THE GAP — grey wait, then green work; read left to right
          as the whole journey, each segment labelled with its own median. */}
      <div
        className="lead-bar"
        role="img"
        aria-label={`Of ${days(l.median)} raised to done, about ${days(l.waitMedian)} waiting in the backlog then ${days(l.workMedian)} being worked`}
      >
        <span className="lead-seg is-wait" style={{ width: `${waitPct}%` }} />
        <span className="lead-seg is-work" style={{ width: `${100 - waitPct}%` }} />
      </div>
      <p className="lead-legend">
        <span className="lead-key"><span className="lead-swatch is-wait" /> Waiting <b>{l.waitMedian}d</b></span>
        <span className="lead-key"><span className="lead-swatch is-work" /> Working <b>{l.workMedian}d</b></span>
      </p>
      <p className="flow-note">
        {waitLeads
          ? <>Most of it is the wait &mdash; a card sits <b>{days(l.waitMedian)}</b> in the backlog before anyone starts.{' '}
              {l.waitP90 >= 1
                ? <>The longest waits run {days(l.waitP90)} or more &mdash; the slowest 1 in 10.</>
                : <>Even the longest waits are under a day.</>}</>
          : <>The work takes longer than the wait &mdash; a card waits <b>{days(l.waitMedian)}</b> before anyone starts, then spends {days(l.workMedian)} being worked.</>}
      </p>
      {seen.length >= 2 && (
        <div className="lead-trend">
          <svg
            className="spark lead-spark"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Lead-time median by month, ${dir} across ${seen.length} months (${seen.map((m) => `${monthName(m.from)} ${m.median}d`).join(', ')})`}
          >
            <polyline points={pts.join(' ')} fill="none" />
          </svg>
          <p className="flow-note lead-trend-note">
            {dir === 'rising'
              ? <>Month by month the lead time is <b>lengthening</b>.</>
              : dir === 'shortening'
                ? <>Month by month the lead time is <b>shortening</b>.</>
                : <>Steady month to month.</>}
          </p>
        </div>
      )}
    </section>
  );
}

/* ── Cumulative Flow: every card by stage, week by week ───────────────────── */

const CF_BANDS: { key: FlowStage; cls: string; label: string }[] = [
  /* Bottom to top: finished work at the floor, not-yet-started at the ceiling —
     so the top edge reads as arrivals and the green floor as departures. Backlog
     is the least-progressed stage of all, so it sits at the very ceiling (above
     To Do). Done stays the green floor and the top edge stays the total; the
     Backlog band only splits the old To Do area. Read top-to-bottom the stack
     matches the backlog sidebar: Backlog · To Do · In Progress · In Review · Done. */
  { key: 'done', cls: 'cf-done', label: 'Done' },
  { key: 'inreview', cls: 'cf-inreview', label: 'In Review' },
  { key: 'staging', cls: 'cf-staging', label: 'In Staging' },
  { key: 'inprogress', cls: 'cf-inprogress', label: 'In Progress' },
  { key: 'todo', cls: 'cf-todo', label: 'To Do' },
  { key: 'backlog', cls: 'cf-backlog', label: 'Backlog' },
];

/* On a board without the In Staging column its band is all-zeros, so it is
   dropped — the stack is the five original bands, exactly as before. It appears
   only once a card has actually been in staging, which is also the only case
   where the all-boards union carries any. */
function cfBandsFor(weeks: FlowWeek[]): typeof CF_BANDS {
  if (weeks.some((w) => w.staging > 0)) return CF_BANDS;
  return CF_BANDS.filter((b) => b.key !== 'staging');
}

/**
 * THE ONE READING JIRA LEAVES TO YOU — arrivals against departures, drawn.
 *
 * The history is fetched on demand (the board read carries only a few days), so
 * this asks for it on mount and draws once it arrives. Every card is stacked
 * into the band it was in each week; the top edge is everything raised, the
 * green floor is everything finished, and the gap between them is the work in
 * progress. The maths is in cumulativeFlow() and checked without a browser.
 */
export function CumulativeFlow() {
  const { issues, windowDays, allBoards } = useInsightsData();
  const flowMoves = useApp((s) => s.flowMoves);
  const loading = useApp((s) => s.flowMovesLoading);
  const loadFlowMoves = useApp((s) => s.loadFlowMoves);
  const boardKey = useApp((s) => s.meta.key);
  /* The scope chip in the header names what the chart is drawing — the board, or
     the all-boards state. Derived exactly as the strip does (the project matching
     the open board, falling back to meta) so the two always agree. boardCount is
     the number of boards the all-boards union is summed over. */
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const meta = useApp((s) => s.meta);
  const boardCount = useApp((s) => s.portfolioIssues.size);
  const currentBoard = projects.find((p) => p.id === projectId);
  const boardName = currentBoard?.name ?? meta.name;
  const chipKey = currentBoard?.key ?? meta.key;
  const boardWord = allBoards ? `across ${boardCount} boards` : 'on the board';
  // Refetch when the board or the scope changes — loadFlowMoves is cached by both
  // and no-ops when the view has not moved.
  useEffect(() => { void loadFlowMoves(); }, [loadFlowMoves, boardKey, allBoards]);

  /* Weekly by default; Daily is the finer scrub Vinnie asked for (25 Sep) — the
     same reconstruction stepped a day at a time. windowDays columns for daily,
     weeksFor(windowDays) for weekly. */
  const [gran, setGran] = useState<'week' | 'day'>('week');
  const daily = gran === 'day';
  const cf = useMemo(
    () => cumulativeFlow(issues, flowMoves ?? [], Date.now(), daily ? windowDays : weeksFor(windowDays), daily ? 1 : 7),
    [issues, flowMoves, windowDays, daily]
  );
  /* Declared here, above the loading/empty early returns, so the hook runs on
     every render (Rules of Hooks) — it keys off the column count either way. */
  const scrub = useScrub(cf.weeks.length);

  /* The scope chip, top-right of the panel header. Single board: the strip's own
     key badge + name. All boards: a distinct state pill (layers glyph on a faint
     --sunk fill) so it reads as a mode, not a board name. A full 1px border, not
     a border-left rail. */
  const scopeChip = allBoards ? (
    <span className="cf-scope cf-scope--all">
      <svg className="cf-scope-ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3 3 8l9 5 9-5-9-5z" />
        <path d="M3 13l9 5 9-5" />
      </svg>
      All my boards
    </span>
  ) : (
    <span className="cf-scope">
      <span className="ins-badge">{chipKey}</span>
      <span className="ins-board-name">{boardName}</span>
    </span>
  );
  const head = (
    <div className="cf-titlerow">
      <div>
        <h2>Cumulative Flow</h2>
        <p className="panel-sub">Every card by stage, {daily ? 'day by day' : 'week by week'} &mdash; arrivals vs departures</p>
      </div>
      {scopeChip}
    </div>
  );

  // Still reading the log, and nothing to draw yet.
  if (flowMoves === null || (loading && flowMoves.length === 0)) {
    return <section className="panel cf-panel">{head}<p className="flow-note">Reading this board&rsquo;s history&hellip;</p></section>;
  }
  const weeks = cf.weeks;
  /* The bands this board actually uses — the full six on a board with In Staging
     turned on, the original five everywhere else (see cfBandsFor). */
  const bands = cfBandsFor(weeks);
  const drawable = weeks.some((w) => w.total > 0);
  if (!drawable) {
    return (
      <section className="panel cf-panel">
        {head}
        <p className="flow-note">
          There isn&rsquo;t enough recorded history on this board yet to draw its flow. It fills in as cards move between columns.
        </p>
      </section>
    );
  }

  const W = 720, H = 200;
  const n = weeks.length;
  const maxTotal = Math.max(1, ...weeks.map((w) => w.total));
  const x = (i: number) => (n > 1 ? (i / (n - 1)) * W : 0);
  const y = (v: number) => H - (v / maxTotal) * H;

  // The cumulative upper edge of each band, per week — the floor of one band is
  // the ceiling of the one below it, which is what makes the areas stack.
  const upper = weeks.map((wk) => {
    let acc = 0;
    const c: Record<string, number> = {};
    for (const b of bands) { acc += wk[b.key]; c[b.key] = acc; }
    return c;
  });
  const bandPath = (bi: number) => {
    const b = bands[bi];
    const top = weeks.map((_, i) => `${x(i).toFixed(1)},${y(upper[i][b.key]).toFixed(1)}`);
    const floor = (i: number) => (bi === 0 ? 0 : upper[i][bands[bi - 1].key]);
    const bottom = weeks.map((_, i) => `${x(i).toFixed(1)},${y(floor(i)).toFixed(1)}`).reverse();
    return `M${top.join(' L')} L${bottom.join(' L')} Z`;
  };

  const a = scrub.active;
  const wk = a != null ? weeks[a] : null;
  const leftPct = (i: number) => (x(i) / W) * 100;
  const chipFor = (t: number) => (daily ? dayChip(t) : `Wk of ${weekOf(t - WEEK_MS)}`);
  const headFor = (t: number) => (daily ? dayLong(t) : `Week of ${weekOf(t - WEEK_MS)}`);
  /* The readout reads wherever the scrub is, and rests on the latest column when
     the cursor has left — one reading, in one fixed place above the plot. */
  const rwk = weeks[a ?? weeks.length - 1];
  /* Faint week markers on the daily view, so a day reads as sitting BETWEEN two
     week boundaries rather than floating. Every 7th column back from the end. */
  const ticks = daily ? weeks.map((_, i) => i).filter((i) => (n - 1 - i) % 7 === 0) : [];

  return (
    <section className="panel cf-panel">
      {head}
      {/* Weekly is the overview; Daily is the fine scrub. A segmented control,
          the same one the scope strip uses, so it reads as the app's own. */}
      <div className="cf-gran seg" role="tablist" aria-label="Chart detail">
        <button role="tab" aria-selected={!daily} className={`seg-btn ${!daily ? 'active' : ''}`} onClick={() => { setGran('week'); scrub.setActive(null); }}>Weekly</button>
        <button role="tab" aria-selected={daily} className={`seg-btn ${daily ? 'active' : ''}`} onClick={() => { setGran('day'); scrub.setActive(null); }}>Daily</button>
      </div>
      {/* THE FIXED READOUT — the column's exact stage breakdown (the arithmetic the
          stacked areas hide), pinned here above the plot so it never covers the
          point you are scrubbing. It follows the cursor's column and rests on the
          latest column when the cursor leaves. */}
      <ScrubReadout
        head={headFor(rwk.at)}
        parts={[...bands].reverse().map((b) => ({ label: b.label, n: rwk[b.key] }))}
        note={<><b>{rwk.total}</b> {boardWord} &middot; <b>{rwk.total - rwk.done}</b> open</>}
      />
      {/* THE BANDS ARE THE SHAPE; the marker follows the cursor. Each column is an
          invisible hit cell reachable by keyboard (role="img" carries the facts in
          words); pointing or focusing one moves the marker and the readout. */}
      <div className="cf-wrap" ref={scrub.ref} onMouseMove={scrub.onMove} onMouseLeave={scrub.onLeave}>
        <svg className="cf-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          {bands.map((b, bi) => <path key={b.key} className={b.cls} d={bandPath(bi)} />)}
        </svg>
        {ticks.map((i) => <div key={`tick-${i}`} className="cf-tick" style={{ left: `${leftPct(i)}%` }} />)}
        {a != null && wk && (() => {
          /* CONTINUOUS geometry, identically for Weekly and Daily — the only thing
             that changes between them is the column count (n); the posX-based
             positioning does not special-case day vs week. The line/dot/chip sit at
             the cursor's own x (posX), and the dot's height rides the top edge by
             interpolating the total between the two flanking columns. The chip
             CONTENT still reads the nearest column (`wk`). */
          const px = scrub.posX ?? leftPct(a) / 100;
          const idx = px * (n - 1);
          const lo = Math.floor(idx), hi = Math.ceil(idx), f = idx - lo;
          const totAt = weeks[lo].total + (weeks[hi].total - weeks[lo].total) * f;
          return (
            <>
              <div className="scrub-line" style={{ left: `${px * 100}%` }} />
              <div className="scrub-dot" style={{ left: `${px * 100}%`, top: `${(y(totAt) / H) * 100}%` }} />
              <div className="scrub-chip" style={{ left: `${px * 100}%` }}>{chipFor(wk.at)}</div>
            </>
          );
        })()}
        <div className="cf-hits">
          {weeks.map((w, i) => (
            <span
              key={w.at}
              className="cf-hit"
              tabIndex={0}
              role="img"
              aria-label={`${headFor(w.at)}: ${w.total} ${boardWord} — ${[...bands].reverse().map((b) => `${w[b.key]} ${b.label.toLowerCase()}`).join(', ')}`}
              onFocus={() => scrub.setActive(i)}
              onBlur={() => scrub.setActive(null)}
            />
          ))}
        </div>
      </div>
      <div className="legend legend-spaced cf-legend">
        {[...bands].reverse().map((b) => (
          <span className="legend-item" key={b.key}>
            <span className={`status-dot ${b.cls}`} /> {b.label}
          </span>
        ))}
      </div>
      <p className="flow-note">
        {cf.arrivalsOutpacing
          ? <>Work is arriving faster than it is closing &mdash; <b>the open band is widening</b>.</>
          : <>Closing about as fast as work arrives &mdash; <b>the top edge is nearly flat</b>.</>}
        {cf.reviewWidening && <> The In&nbsp;Review band is widening, the first sign of a review bottleneck.</>}
      </p>
    </section>
  );
}

/* ── One chart, split whichever way answers the question ──────────────────── */

export function WhereTheWork() {
  const { issues, allBoards } = useInsightsData();
  const areas = useApp((s) => s.areas);
  const profiles = useApp((s) => s.profiles);
  const setFilters = useApp((s) => s.setFilters);
  const filters = useApp((s) => s.filters);
  const setViewId = useApp((s) => s.setViewId);
  const drillTo = useApp((s) => s.drillTo);
  const showInBacklog = useApp((s) => s.showInBacklog);
  const portfolioIssues = useApp((s) => s.portfolioIssues);
  const portfolioAreas = useApp((s) => s.portfolioAreas);
  const [by, setBy] = useState<Dimension>('area');

  /*
   * ACROSS BOARDS, AREA SURVIVES BY NAME — #462. Area is a per-board id, so the
   * union cannot group by it directly: another board's cards carry ids this
   * board's list has never heard of. `splitByAreaName` resolves each card's id to
   * a NAME within its own board — the same roll-up the Portfolio Areas lens makes
   * — so one area that runs on three boards is one bar. Epic, Type, Assignee and
   * Stage already survive: they are a shared word, an email or a workflow state.
   */
  const dims = Object.keys(DIMENSION_LABEL) as Dimension[];
  const activeBy = dims.includes(by) ? by : 'status';

  const issueTypes = useApp((s) => s.issueTypes);
  const rows = useMemo(
    () =>
      allBoards && activeBy === 'area'
        ? splitByAreaName(portfolioIssues, portfolioAreas, issueTypes)
        : splitBy(issues, activeBy, areas, profiles, issueTypes),
    [allBoards, issues, activeBy, areas, profiles, issueTypes, portfolioIssues, portfolioAreas]
  );
  const most = Math.max(1, ...rows.map((r) => r.total));

  /*
   * EVERY BAR IS A WAY INTO THE BACKLOG NOW, by two different routes.
   *
   * Area and Epic set a real FILTER, which is better because it persists, shows
   * as a pill and can be taken off. The other three cannot: the filter bar has
   * no control for Type, Assignee or Stage, and that is #301.
   *
   * They used to be dead rows for exactly that reason — a drill-down landing on
   * an unfiltered list is worse than none, because the list looks like an
   * answer. `showInBacklog` closed that: it shows exactly these cards and says
   * on the screen what they have in common, which is precise rather than
   * unfiltered. So the reason for the dead rows is gone, and with it a hover
   * card that would have said "click to open" on some rows and not others.
   */
  /*
   * ── FOUR OF THE FIVE ARE REAL FILTERS NOW — #301 ────────────────────────
   *
   * This used to read: *"the filter bar has no control for Type, Assignee or
   * Stage, and that is #301."* #301 built three of them. So Type and Assignee
   * stop being a NAMED SET of card ids and become an actual filter, which is
   * strictly better in three ways — it shows as a pill, it can be taken off one
   * field at a time, and it survives the next click instead of evaporating.
   *
   * STATUS STAYED A NAMED SET, and that is the one worth explaining. `splitBy`
   * groups by STAGE, which is not the same question as the status filter: a
   * row of "In Review" from this chart is the exact set of cards the chart
   * counted, and handing that to a status filter would quietly re-count it
   * against the current view. A drill-down that shows a DIFFERENT number from
   * the bar you clicked is worse than one that cannot be taken off.
   */
  // The backlog is one board, so a bar counted across every board cannot drill
  // into it honestly — 175 on the chart landing on the 30 that happen to be this
  // board reads as the chart lying. Across boards the bars are a picture, not a
  // way in; on one board they open exactly what they count, as before.
  const canDrill = !allBoards;
  const filterable = canDrill && (activeBy === 'area' || activeBy === 'epic' || activeBy === 'kind' || activeBy === 'assignee');
  /**
   * ⚠️ THE LIST MUST COME TO THE SAME NUMBER AS THE BAR THAT WAS CLICKED.
   *
   * `splitBy` counts OPEN work only (`!isClosed`, src/insights.ts), which is the
   * exact set the 'Open' view shows. So the drill lands on 'Open', NOT
   * 'Everything': landing on Everything folded the finished cards back in — the
   * ones the bar never counted — so a bar reading 144 opened a list of 271. The
   * interaction-witness caught this on 24 September 2026, and the Stage branch
   * below was already right because it hands over the bar's own open id set via
   * `showInBacklog`.
   *
   * And it REPLACES the filters rather than adding to them (only the sort
   * survives, because sort hides nothing), so a filter left on from an earlier
   * visit cannot narrow it further and sit as a pill above a number the reader
   * has already dismissed.
   */
  const only = (patch: Partial<Filters>) =>
    setFilters({ ...DEFAULT_FILTERS, sort: filters.sort, ...patch });
  const open = (r: SplitRow) => {
    if (!canDrill) return;
    if (activeBy === 'area') only({ area: [r.key === NO_AREA ? FILTER_NO_AREA : r.key] });
    else if (activeBy === 'epic') only({ epic: r.key === NO_AREA ? [] : [r.key] });
    else if (activeBy === 'kind') only({ type: [r.key] as never });
    else if (activeBy === 'assignee') only({ assignee: [r.key === NO_AREA ? FILTER_NOBODY : r.key] });
    else {
      showInBacklog(r.ids, `${DIMENSION_LABEL[activeBy].toLowerCase()} ${r.label}`);
      return;
    }
    setViewId('open');
    drillTo('backlog');
  };

  return (
    <section className="panel">
      <h2>Where the work is</h2>
      <div className="seg where-seg" role="tablist">
        {dims.map((d) => (
          <button
            key={d}
            role="tab"
            aria-selected={activeBy === d}
            className={`seg-btn ${activeBy === d ? 'active' : ''}`}
            onClick={() => setBy(d)}
          >
            {DIMENSION_LABEL[d]}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="hint">Nothing open to split up.</p>
      ) : (
        rows.map((r) => {
          const inner = (
            <>
              {r.colour && <span className="area-dot" style={{ background: r.colour }} />}
              <span className="where-name">{r.label}</span>
              <span className="where-track">
                {/* Length says how much. The darker head says how much of that
                    is fixing rather than building, which is the question the
                    old "bug share" panel existed to answer on its own. */}
                <span className="where-fill" style={{ width: `${(r.total / most) * 100}%` }}>
                  {r.fixing > 0 && (
                    <span className="where-bugs" style={{ width: `${(r.fixing / r.total) * 100}%` }} />
                  )}
                </span>
              </span>
              <span className="where-n">{r.total}</span>
              {canDrill && (
                <Card
                  head={r.label}
                  sub={
                    <>
                      {r.total} open
                      {/* THE MEANING, NOT A TYPE NAME — #302. Saying "3 bugs" on a
                          board whose fixing types are "Defect" and "Incident" would
                          name a type it does not have. */}
                      {r.fixing > 0 && <> · <b>{r.fixing} fixing something broken</b></>}
                    </>
                  }
                >
                  {/* Splitting BY stage and then saying the stage back would be the
                      row's own name repeated underneath it. */}
                  {activeBy !== 'status' && <Parts of={r.stages} />}
                  <span className="hc-cta">
                    {filterable ? `Click to filter the backlog to it →` : `Click to open all ${r.total} →`}
                  </span>
                </Card>
              )}
            </>
          );
          const label =
            `${r.label}: ${r.total} open` +
            (r.fixing > 0 ? `, ${r.fixing} of them fixing something broken` : '') +
            (activeBy === 'status' ? '' : `. ${say(r.stages)}`);
          /* Across boards the bar is a reading, not a way in — the backlog is one
             board and could not honour the click — so it is a plain row. */
          return canDrill ? (
            <button
              key={r.key}
              type="button"
              className="where-row can-open hc-anchor"
              aria-label={`${label}. Open them.`}
              onClick={() => open(r)}
            >
              {inner}
            </button>
          ) : (
            <div key={r.key} className="where-row" aria-label={label}>
              {inner}
            </div>
          );
        })
      )}
      <p className="hint">
        Darker part of a bar is work that fixes something broken.
        {canDrill ? ' Click a row to open it in the backlog.' : ' Across boards, the bars are a reading — open a single board to click into one.'}
      </p>
    </section>
  );
}

/* ── The way back ─────────────────────────────────────────────────────────── */

/**
 * THE ROUTE BACK FROM A DRILL-DOWN, asked for by name.
 *
 * Vinnie, 27 August 2026: *"when you drill down and change screens there should
 * be a way back to where you were rather than finding it again in a menu."*
 *
 * SEPARATE FROM THE FILTER CHIP, which was the temptation. Taking the filter
 * off leaves you on the backlog looking at everything; going back returns you
 * to the screen you came from. Two different things, so two different controls
 * — one control doing both means one of the two is unreachable.
 *
 * Renders nothing when nobody drilled anywhere, so it never sits on the screen
 * offering to return you somewhere you left on purpose.
 */
export function WayBack() {
  const cameFrom = useApp((s) => s.cameFrom);
  const view = useApp((s) => s.view);
  const setView = useApp((s) => s.setView);
  /* Nothing to go back to, or the trail would point at the very screen you are
     on (a reveal-in-place drill) — either way, offer no return. */
  if (!cameFrom || cameFrom === view) return null;
  /* The screen name comes from the one place every surface reads it
     (src/view-titles.ts), so this trail can never disagree with the nav about
     what a screen is called — it used to keep its own partial list and offered
     "Back to approvals" for the screen the nav calls "Sign-offs". */
  return (
    <button className="way-back" onClick={() => setView(cameFrom)}>
      <span aria-hidden="true">←</span> Back to {wayBackLabel(cameFrom)}
    </button>
  );
}

/* ── FORECAST FINISH: the Monte Carlo S-curve (#434) ──────────────────────── */

const dm = (t: number) => { const d = new Date(t); return `${d.getDate()} ${MONTHS[d.getMonth()]}`; };
const yr = (t: number) => String(new Date(t).getFullYear());

/* A holiday's own YYYY-MM-DD, read at local midnight, as "24 Dec" — for the
   working-days foot-cue, which names the close that moved the predicted date. */
const dmISO = (iso: string) => { const [, m, d] = iso.split('-').map(Number); return `${d} ${MONTHS[(m || 1) - 1]}`; };
const closeSpan = (h: Holiday) => (h.until && h.until !== h.date ? `${dmISO(h.date)} – ${dmISO(h.until)}` : dmISO(h.date));
const TREND_WORD: Record<Forecast['trend'], string> = {
  steady: 'steady',
  speeding: 'speeding up',
  slowing: 'slowing',
};
const VERDICT_WORD: Record<'good' | 'warn' | 'crit', string> = {
  good: 'On track for Go-Live',
  warn: 'At risk of the target',
  crit: 'Unlikely to make it',
};
/* Map the forecast's verdict onto the app's standard status badge (.rag g/a/r). */
const RAG_LETTER: Record<'good' | 'warn' | 'crit', 'g' | 'a' | 'r'> = { good: 'g', warn: 'a', crit: 'r' };

/* THE SHARED DATE AXIS. The band, the reader and the histogram all plot against
   the same span forecast() returns (curve[0]..curve[last]), so a date sits at the
   same x in every view and the reference lines line up by construction. */
function useAxis(f: Forecast) {
  const dom0 = f.curve[0].at;
  const dom1 = f.curve[f.curve.length - 1].at;
  const span = Math.max(1, dom1 - dom0);
  const xf = (t: number) => Math.max(0, Math.min(1, (t - dom0) / span));
  const tAt = (frac: number) => dom0 + span * Math.max(0, Math.min(1, frac));
  return { dom0, dom1, span, xf, tAt };
}

/* Five evenly spaced date reads across the span, snapped to whole days so a tick
   never shows a fractional time. */
function axisTicks(dom0: number, dom1: number, n = 5): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(dom0 + ((dom1 - dom0) * i) / (n - 1));
    d.setHours(0, 0, 0, 0);
    out.push(d.getTime());
  }
  return out;
}

/* CHANCE OF FINISHING BY A DATE, read off the curve by linear interpolation. curve
   is 60 evenly-spaced cumulative samples over the span, so a fractional position
   interpolates to a smooth, CONTINUOUS probability — the reader never steps from
   one sample to the next, which is what lets the bar glide. */
function chanceByFrac(f: Forecast, frac: number): number {
  const c = f.curve;
  const n = c.length - 1;
  const x = Math.max(0, Math.min(1, frac)) * n;
  const i = Math.min(n - 1, Math.floor(x));
  return c[i].p + (c[i + 1].p - c[i].p) * (x - i);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * THE CONFIDENCE READER — a bar that GLIDES under the pointer (Vinnie, 29 Sep:
 * "slides smoothly as we did for the CFD, no jumping"). Exactly the CFD scrub
 * technique: track the pointer's CONTINUOUS position (`frac`, 0..1 of the plot
 * width) and draw the bar at that x with NO CSS transition on `left`, so it sits
 * under the finger with no lag and no stepping. The date and chance are read from
 * that same continuous frac. `null` is at rest — the reader is hidden and the
 * panel stays calm until the user reaches for it (hover, drag or focus). Keyboard
 * moves it a whole day at a time so it is reachable without a pointer.
 */
function useReader(span: number) {
  const [frac, setFrac] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const fracFromClientX = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return r.width > 0 ? Math.max(0, Math.min(1, (clientX - r.left) / r.width)) : 0;
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    /* A hovering mouse tracks freely; a finger only tracks once it is down, so a
       scroll-drag past the card does not hijack it. Either way the bar sits under
       the pointer with no transition — the glide is the pointer's own motion. */
    if (e.pointerType !== 'mouse' && !dragging.current) return;
    setFrac(fracFromClientX(e.clientX));
  }, [fracFromClientX]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* pre-capture browsers */ }
    setFrac(fracFromClientX(e.clientX));
  }, [fracFromClientX]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    dragging.current = false;
    /* A touch leaves nothing hovering, so lift the reader away on release; a mouse
       keeps reading until it leaves the plot. */
    if (e.pointerType !== 'mouse') setFrac(null);
  }, []);

  const onPointerLeave = useCallback(() => { if (!dragging.current) setFrac(null); }, []);

  const dayStep = span > 0 ? DAY_MS / span : 0.02;
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    setFrac((prev) => {
      const cur = prev ?? 0.5;
      if (e.key === 'ArrowLeft') { e.preventDefault(); return Math.max(0, cur - dayStep); }
      if (e.key === 'ArrowRight') { e.preventDefault(); return Math.min(1, cur + dayStep); }
      if (e.key === 'Home') { e.preventDefault(); return 0; }
      if (e.key === 'End') { e.preventDefault(); return 1; }
      if (e.key === 'Escape') return null;
      return prev;
    });
  }, [dayStep]);

  return { frac, ref, onPointerMove, onPointerDown, onPointerUp, onPointerLeave, onKeyDown };
}

/**
 * WHAT WOULD MAKE AN AT-RISK GO-LIVE SAFE — a first-order read, not a second
 * forecast. The finish spread scales with (scope ÷ pace): to pull the 85% date
 * back onto the target you raise the pace or cut the scope in the same proportion.
 * Both are rounded and hedged ("about", "~") because they are guidance, not a
 * promise — the honest maths stays the Monte Carlo the band is drawn from. Returns
 * null when there is nothing useful or honest to say (no target, or already safe).
 */
function safeMaking(f: Forecast, now: number): { pace: number; scope: number } | null {
  if (f.target == null || f.p85 <= f.target) return null;
  const w85 = (f.p85 - now) / (7 * DAY_MS);
  const wTgt = (f.target - now) / (7 * DAY_MS);
  if (wTgt <= 0 || w85 <= 0) return null;
  const extra = Math.max(1, Math.round(f.perWeek * (w85 / wTgt) - f.perWeek));
  const trim = Math.max(1, f.remaining - Math.round(f.remaining * (wTgt / w85)));
  return { pace: extra, scope: trim };
}

/**
 * THE LIKELY-FINISH BAND — the calm primary. A horizontal range from the early edge
 * (10%) to near-certain (95%), a denser core from the coin-flip (50%) to the
 * commit-grade date (85%), ONE called-out marker at 85%, and — when the project has
 * a go-live — the target as a brass reference line. Everything else stays out until
 * the reader is engaged: on engagement the reader glides under the pointer and, when
 * the target is at risk, the one "what would make it safe" line appears. At rest it
 * is just the answer and the range.
 */
function ForecastBand({ f, hasTarget, safe }: { f: Forecast; hasTarget: boolean; safe: { pace: number; scope: number } | null }) {
  const { xf, tAt, span } = useAxis(f);
  const rd = useReader(span);
  const ticks = axisTicks(f.curve[0].at, f.curve[f.curve.length - 1].at);

  const engaged = rd.frac != null;
  const readerFrac = (rd.frac ?? 0) as number;
  const readerT = engaged ? tAt(readerFrac) : 0;
  const readerP = engaged ? chanceByFrac(f, readerFrac) : 0;
  const p85x = xf(f.p85);
  /* Open the reader tooltip leftward when it is near the 85% callout, so the two
     never overlap. */
  /* Keep the reader tooltip off the 85% callout: when the reader is close to it,
     open the tooltip AWAY from it — leftward when the reader is at or left of the
     marker (the callout is then to its right), rightward when the reader is past
     it. Far from the marker the tooltip stays centred on the reader. */
  const dMark = readerFrac - p85x;
  const shiftLeft = engaged && dMark > -0.18 && dMark <= 0.02;
  const shiftRight = engaged && dMark > 0.02 && dMark < 0.18;

  return (
    <div className="fc-bandwrap">
      <div
        className="fc-plotw"
        ref={rd.ref}
        role="slider"
        tabIndex={0}
        aria-label="Read the chance of finishing by any date"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={engaged ? Math.round(readerP * 100) : 85}
        aria-valuetext={engaged ? `${Math.round(readerP * 100)}% chance of finishing by ${dm(readerT)}` : `85% confident by ${dm(f.p85)}`}
        onPointerMove={rd.onPointerMove}
        onPointerDown={rd.onPointerDown}
        onPointerUp={rd.onPointerUp}
        onPointerLeave={rd.onPointerLeave}
        onKeyDown={rd.onKeyDown}
      >
        {/* The full draggable range, drawn as a track so its extent is visible; the
            band highlights the likely window on top of it. */}
        <div className="fc-track" />
        <div className="fc-band-outer" style={{ left: `${xf(f.p10) * 100}%`, width: `${(xf(f.p95) - xf(f.p10)) * 100}%` }} />
        <div className="fc-band-inner" style={{ left: `${xf(f.p50) * 100}%`, width: `${(p85x - xf(f.p50)) * 100}%` }} />

        {/* the one called-out marker: commit-grade 85% */}
        <div className="fc-pmark" style={{ left: `${p85x * 100}%` }} />
        <div className="fc-callout" style={{ left: `${p85x * 100}%` }}>
          <div className="fc-callout-pct">85% confident</div>
          <div className="fc-callout-dt">{dm(f.p85)}</div>
        </div>

        {hasTarget && (
          <div className="fc-target" style={{ left: `${xf(f.target as number) * 100}%` }}>
            <div className="fc-target-line" />
            <div className="fc-target-lab"><div className="a">Go-Live</div><div className="d">{dm(f.target as number)}</div></div>
          </div>
        )}

        {engaged && (
          <>
            <div className="fc-reader" style={{ left: `${readerFrac * 100}%` }}>
              <div className="fc-reader-line" />
              <div className={`fc-tip${shiftLeft ? ' shift-left' : ''}${shiftRight ? ' shift-right' : ''}`}>
                <span className="big">{Math.round(readerP * 100)}% chance</span>
                <span className="of">of finishing by {dm(readerT)}</span>
                <span className="hint">Drag to test any date</span>
              </div>
            </div>
            {/* The knob stays wholly on the track at the extremes (clamped by its own
                half-width), while the reader line above marks the exact date. */}
            <div
              className="fc-knob"
              aria-hidden="true"
              style={{ left: `clamp(var(--fc-knob-half), ${readerFrac * 100}%, calc(100% - var(--fc-knob-half)))` }}
            >
              <span className="chev">&#8249;</span><span className="grip" /><span className="chev">&#8250;</span>
            </div>
          </>
        )}
      </div>

      <div className="fc-axis">
        {ticks.map((t, i) => (
          <span className="fc-atick" key={i} style={{ left: `${xf(t) * 100}%` }}><span className="stem" />{dm(t)}</span>
        ))}
      </div>

      {engaged && hasTarget && f.rag !== 'good' && safe && (
        <div className="fc-wit">
          <div className="fc-wit-h">What would make {dm(f.target as number)} safe</div>
          <div className="fc-wit-b">Lift it to <b>85%</b> with about <span className="em">{safe.pace} more card{safe.pace === 1 ? '' : 's'} a week</span>, or trim <span className="em">~{safe.scope} card{safe.scope === 1 ? '' : 's'}</span> of scope.</div>
        </div>
      )}
    </div>
  );
}

/**
 * THE SPREAD, ON DEMAND — the drill-down behind the band. Each bar is the chance the
 * work finishes IN that date window (from f.spread), so the bars are pure likelihood
 * and sum across the axis; they are NEVER a count of runs. The tallest window is
 * called out in brand; the coin-flip, the 85% date and the go-live sit as reference
 * lines on the same axis. The y-axis is labelled "Likelihood".
 */
/**
 * THE CUMULATIVE LINE AS A SMOOTH PATH. The curve is sampled from a bootstrap
 * (an EMPIRICAL CDF), so its slope jitters point-to-point and a raw polyline looks
 * wavy even though it only ever rises. Two presentation-only steps fix that without
 * touching any stated number (percentiles/target come from the maths, not this
 * line): a short moving average settles the slope, a monotonic clamp keeps a CDF
 * from ever dipping, and a Catmull-Rom→bézier pass draws one clean S-curve. Points
 * are in the 0–100 viewBox space; x is the shared axis, y is 100·(1−p).
 */
function smoothCumPath(curve: { at: number; p: number }[], xf: (at: number) => number): string {
  const n = curve.length;
  if (n === 0) return '';
  const xs = curve.map((pt) => xf(pt.at) * 100);
  const yRaw = curve.map((pt) => (1 - pt.p) * 100);
  const R = 4; // moving-average radius; window shrinks at the ends so 0%/100% hold
  const ys = yRaw.map((_, i) => {
    let sum = 0, c = 0;
    for (let k = -R; k <= R; k++) { const j = i + k; if (j >= 0 && j < n) { sum += yRaw[j]; c++; } }
    return sum / c;
  });
  for (let i = 1; i < n; i++) if (ys[i] > ys[i - 1]) ys[i] = ys[i - 1]; // a CDF only rises
  const P: [number, number][] = xs.map((x, i) => [x, ys[i]]);
  let d = `M ${P[0][0].toFixed(2)} ${P[0][1].toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = P[i - 1] ?? P[i];
    const p1 = P[i];
    const p2 = P[i + 1];
    const p3 = P[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return d;
}

export function ForecastHistogram({ f, hasTarget }: { f: Forecast; hasTarget: boolean }) {
  const { xf } = useAxis(f);
  /* Watch the plot's real width so the labels and axis can thin out when the card
     is narrow — the markers cluster (p50, go-live and p85 can be days apart) and
     their labels would otherwise overprint. This measures the drawn width, not the
     viewport, so it is right whatever the sidebar or split is doing. */
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotW, setPlotW] = useState(0);
  useEffect(() => {
    const el = plotRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPlotW(el.clientWidth));
    ro.observe(el);
    setPlotW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const narrow = plotW > 0 && plotW < 360;

  const allTicks = axisTicks(f.curve[0].at, f.curve[f.curve.length - 1].at);
  /* Every other tick when the plot is narrow, so the date labels never touch. */
  const ticks = narrow ? allTicks.filter((_, i) => i % 2 === 0) : allTicks;
  const maxP = f.spread.reduce((m, b) => Math.max(m, b.p), 0);
  /* A tidy y-axis top a little above the tallest window, rounded to a clean 5%. */
  const topPct = Math.max(10, Math.ceil((maxP * 100 + 4) / 5) * 5);
  const grid: number[] = [];
  for (let g = 0; g <= topPct; g += 5) grid.push(g);

  /* THE CUMULATIVE LINE — "chance it is ALL done by this date" — drawn over the
     bars on its own 0–100% right axis (a Pareto pairing). Each bar is one week's
     small share of the whole spread, which is why the tallest is only ~17%; the
     line is the running total those shares add up to, so it climbs to 100% and
     answers "17% of what?" at a glance. f.curve is the CDF already (p is 0..1,
     chance finished BY that date), so the line reads straight off it. viewBox is
     0..100 in both axes and stretched to the plot; the stroke stays 2px via
     non-scaling-stroke. */
  const cumPath = smoothCumPath(f.curve, xf);
  const rGrid = [0, 25, 50, 75, 100];

  /* THE REFERENCE MARKERS — 50%, the go-live target (when set) and 85%. Their
     dates can sit only days apart, so a centred label per line overprints the
     next as the card narrows. Give each label its own row instead, stacked in
     date order (earliest lowest), so they never share a horizontal line at ANY
     width. `.fc-hist` reserves the headroom for three rows above the plot. */
  const marks = [
    { key: 'p50', cls: 'p', at: f.p50, lab: '50%' },
    ...(hasTarget ? [{ key: 'go', cls: 'g', at: f.target as number, lab: 'Go-Live' }] : []),
    { key: 'p85', cls: 'p', at: f.p85, lab: '85%' },
  ].sort((a, b) => a.at - b.at);

  return (
    <div className={`fc-hist${narrow ? ' is-narrow' : ''}`}>
      <div className="fc-hist-yttl">Chance that week</div>
      <div className="fc-hist-yttl r">Chance done by then</div>
      <div className="fc-hist-plot" ref={plotRef}>
        {grid.map((g) => (
          <div className="fc-hist-grid" key={g} style={{ bottom: `${(g / topPct) * 100}%` }}>
            <span className="fc-hist-gcap">{g}%</span>
          </div>
        ))}
        {rGrid.map((r) => (
          <span className="fc-hist-rcap" key={r} style={{ bottom: `${r}%` }}>{r}%</span>
        ))}
        <div className="fc-hist-bars">
          {f.spread.map((b, i) => {
            const pctLabel = Math.round(b.p * 100);
            const peak = b.p === maxP && maxP > 0;
            return (
              <div className="fc-bcol" key={i}>
                {pctLabel >= 8 && <span className="fc-bcap">{pctLabel}%</span>}
                <div className={`fc-bar${peak ? ' peak' : ''}`} style={{ height: `${(b.p / (topPct / 100)) * 100}%` }} />
              </div>
            );
          })}
        </div>
        <svg className="fc-hist-cum" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <path d={cumPath} />
        </svg>
        {marks.map((m, i) => (
          <div className={`fc-hov ${m.cls}`} key={m.key} style={{ left: `${xf(m.at) * 100}%` }}>
            <span className="lab" style={{ top: `${-22 - i * 19}px` }}>{m.lab}<span className="d"> · {dm(m.at)}</span></span>
          </div>
        ))}
      </div>
      <div className="fc-axis fc-hist-axis">
        {ticks.map((t, i) => (
          <span className="fc-atick" key={i} style={{ left: `${xf(t) * 100}%` }}><span className="stem" />{dm(t)}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * FORECAST FINISH — when the remaining work is likely to be done, and whether the
 * go-live target will hold. The maths (a bootstrap Monte Carlo over this board's
 * throughput) is in src/insights.ts so it can be checked without a browser; what
 * is here is the drawing. Approved band + histogram design, #434.
 */
export function Forecast() {
  const { issues, windowDays, allBoards } = useInsightsData();
  /* The go-live target is the project's own field (decision #1), resolved through its
     plan anchor (0217) so a phase/milestone-anchored board forecasts against the date
     the plan actually sets — not a stale or blank typed date. Across boards there is no
     single target, so the verdict is omitted and the curve stands on its own — the same
     no-target path a board that has set no date takes. */
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const target = useMemo(() => {
    if (allBoards || !project) return null;
    /* ONE resolver, shared with the per-card release odds (goLiveTargetMs) so the
       panel and a card's "will it make the release?" can never read different days:
       the anchor (phase end → milestone day → typed date) placed at LOCAL midnight,
       the same now-relative axis the Monte-Carlo percentiles live on. */
    return goLiveTargetMs(
      project,
      livePhases(phases),
      (id) => milestones.find((m) => m.id === id)?.targetDay,
    );
  }, [allBoards, project, phases, milestones]);
  /* The workspace working calendar (0216) — so a known future close pushes the
     predicted finish out, and the foot-cue can say which one. Inert default → the
     forecast reads exactly as it did before a calendar existed. */
  const workCalendar = useApp((s) => s.workCalendar);

  /* BACKLOG scope is mode-keyed (Vinnie, 30 Oct): a PROJECT counts its backlog as
     planned work still to finish (so a project with work parked in the backlog
     cannot forecast "nearly done" and hide a go-live miss); a CONTINUOUS board's
     backlog is an open-ended candidate pool, so it leaves the remaining scope.
     Unset reads as a project (see ProjectMeta.deliveryMode). Across boards there is
     no single mode, so the blended curve keeps the default (include). */
  const countBacklog = allBoards || project?.deliveryMode !== 'continuous';

  /* Seeded from the board key so the forecast is deterministic per board — the
     headline date must not jitter between reloads when the data has not moved. */
  const f = useMemo(
    () => forecast(issues, Date.now(), target, weeksFor(windowDays), undefined, undefined, projectId ?? '', undefined, workCalendar, countBacklog),
    [issues, windowDays, target, projectId, workCalendar, countBacklog]
  );

  return <ForecastView f={f} />;
}

/**
 * The panel's drawing, a pure function of the forecast result — no store, so a
 * render harness can screenshot every state (green / amber / red / cold start)
 * from the real component rather than a mock. The store-connected Forecast()
 * above computes `f` and hands it here.
 */
export function ForecastView({
  f,
  title = 'Forecast Finish',
  sub = 'When this board’s remaining work is likely to be done, from its own delivery history',
}: { f: Forecast; title?: string; sub?: string }) {

  if (!f.ready) {
    /* Nothing left to finish is a real, different answer from too-little-history;
       saying "not enough finished work" over a completed board would be a lie. */
    if (f.remaining === 0 && f.finished > 0) {
      return (
        <section className="panel">
          <h2>{title}</h2>
          <p className="panel-sub">{sub}</p>
          <div className="fc-cold">
            <div className="fc-cold-ico">
              <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <div>
              <p className="fc-cold-head">Nothing left to forecast</p>
              <p className="flow-note" style={{ maxWidth: 540 }}>
                Every card on this board is finished or set aside, so there is no remaining work to put a
                date on.
              </p>
            </div>
          </div>
        </section>
      );
    }
    const progress = Math.round(
      Math.min(1, Math.min(f.finished / FORECAST_MIN_FINISHED, f.weeksWithFinishes / FORECAST_MIN_WEEKS)) * 100
    );
    return (
      <section className="panel">
        <h2>{title}</h2>
        <p className="panel-sub">{sub}</p>
        <div className="fc-cold">
          <div className="fc-cold-ico">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
          </div>
          <div>
            <p className="fc-cold-head">Not enough finished work to forecast yet</p>
            <p className="flow-note" style={{ maxWidth: 540 }}>
              A dependable date needs at least a few weeks of cards actually finishing. This board has
              completed <b>{f.finished} card{f.finished === 1 ? '' : 's'} over {f.weeksWithFinishes} week
              {f.weeksWithFinishes === 1 ? '' : 's'}</b> &mdash; too few, and too short, to simulate a spread
              that would not mislead.
            </p>
            <p className="fc-cold-prog">
              <span className="fc-mini"><i style={{ width: `${progress}%` }} /></span>{' '}
              Forecasting turns on at about <b>&nbsp;{FORECAST_MIN_FINISHED} finished cards across{' '}
              {FORECAST_MIN_WEEKS} weeks</b>.
            </p>
            <p className="flow-note" style={{ marginTop: 12, color: 'var(--t3)' }}>
              In the meantime, <b style={{ color: 'var(--t2)' }}>Throughput</b> and{' '}
              <b style={{ color: 'var(--t2)' }}>Net flow</b> show the pace as it builds.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const hasTarget = f.target != null && f.rag != null;
  const [open, setOpen] = useState(false);
  const safe = hasTarget ? safeMaking(f, Date.now()) : null;
  /* The centre of the most likely window — the plain-language "around when" for
     the drill-down lede. */
  const peakMid = f.spread.length
    ? (() => { const b = f.spread.reduce((a, x) => (x.p > a.p ? x : a), f.spread[0]); return (b.from + b.to) / 2; })()
    : f.p50;

  return (
    <section className="panel">
      <h2>{title}</h2>
      <p className="panel-sub">{sub}</p>
      <div className="fc-body">
        <div className="fc-summary">
          {hasTarget && (
            <span className={`rag ${RAG_LETTER[f.rag as 'good' | 'warn' | 'crit']} fc-verdict`}>
              <span className="dot" />{VERDICT_WORD[f.rag as 'good' | 'warn' | 'crit']}
            </span>
          )}
          <p className="fc-cap">Likely finish</p>
          <div className="fc-headline">{dm(f.p85)} <span className="fc-yr">{yr(f.p85)}</span></div>
          <p className="fc-conf"><b>85%</b> confident</p>
          <p className="fc-subline">50% by {dm(f.p50)} &middot; 95% by {dm(f.p95)}</p>
          <p className="fc-scope">
            <span className="k"><b>{f.remaining} card{f.remaining === 1 ? '' : 's'}</b> left to finish</span>
            {/* Name the window the trend is read over (pm-witness L194, 1 Nov): the
                Throughput panel says "slowing over 8 weeks" while this forecast reads
                a different span, so without the window the two look like they disagree.
                With both labelled, they read as what they are — two spans, not a clash. */}
            <span className="k">Finishing <b>~{f.perWeek} a week</b>, {TREND_WORD[f.trend]} over {f.weeks} weeks</span>
          </p>
        </div>

        <div className="fc-chart">
          {!open ? (
            <>
              <p className="fc-lede">
                {hasTarget ? (
                  <>
                    The <span className="g">{dm(f.target as number)} Go-Live</span> has{f.rag === 'good' ? ' a' : ''}{' '}
                    <b>{Math.round((f.targetProb as number) * 100)}% chance</b> of being met.
                  </>
                ) : (
                  <>From this board&rsquo;s own delivery pace. <b>Point at any date</b> to read the chance of finishing by then.</>
                )}
              </p>
              <ForecastBand f={f} hasTarget={hasTarget} safe={safe} />
              <div className="fc-foot">
                <span className="fc-basis">Based on the last {f.weeks} weeks&rsquo; pace</span>
                <button type="button" className="btn btn-small fc-expand" onClick={() => setOpen(true)} aria-expanded={false}>
                  Show the spread<span className="cv" aria-hidden="true">&rsaquo;</span>
                </button>
              </div>
              {/* THE WORKING-DAYS CUE — shown ONLY when a known close actually pushed
                  the predicted date out (Vinnie, 28 Oct), naming the close so the move
                  is explained, not just asserted. Silent on an ordinary forecast. */}
              {f.holidayMoved && (
                <p className="fc-wd">
                  <Icon name="calendar" size={14} />
                  <span>
                    {f.holidayCloses && f.holidayCloses.length === 1 ? (
                      <><b>Steps over {f.holidayCloses[0].name}</b> ({closeSpan(f.holidayCloses[0])}), </>
                    ) : f.holidayCloses && f.holidayCloses.length > 1 ? (
                      <><b>Steps over {f.holidayCloses.length} scheduled closures</b>, </>
                    ) : (
                      <><b>Steps over a scheduled closure</b>, </>
                    )}
                    so the date lands ~{f.holidayDays} working day{f.holidayDays === 1 ? '' : 's'} later than the raw pace.
                  </span>
                </p>
              )}
            </>
          ) : (
            <>
              <button type="button" className="btn btn-small fc-drillnote" onClick={() => setOpen(false)} aria-expanded>
                <span className="cv" aria-hidden="true">&lsaquo;</span>The spread behind the forecast
              </button>
              <p className="fc-lede">
                Each bar is the chance of finishing <b>that week</b> &mdash; busiest around <b>{dm(peakMid)}</b>. The line is the chance it&rsquo;s <b>all done by then</b>, climbing to <b>85% by {dm(f.p85)}</b>.
              </p>
              <ForecastHistogram f={f} hasTarget={hasTarget} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
